import React from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { ProtectedRoute, PublicRoute } from '@/components/ProtectedRoute';
import SubscribeSuccessPage from '@/pages/SubscribeSuccessPage';

// Pages
import LandingPage from '@/pages/LandingPage';
import LoginPage from '@/pages/LoginPage';
import SignupPage from '@/pages/SignupPage';
import DashboardPage from '@/pages/DashboardPage';
import SettingsPage from '@/pages/SettingsPage';
import AdminPage from '@/pages/AdminPage';
import AdminAnalyticsPage from '@/pages/AdminAnalyticsPage';
import AdminOperationsPage from '@/pages/AdminOperationsPage';
import CostIntelligencePage from '@/pages/CostIntelligencePage';
import DemoPage from '@/pages/DemoPage';
import PricingPage from '@/pages/PricingPage';
// import OnboardingPage from '@/pages/OnboardingPage'; // deleted — legacy 4-step wizard retired Round 218A P0 (2026-02-07). Business Builder Bot IS the onboarding.
import MyCreationsPage from '@/pages/MyCreationsPage';
import MetricoolSchedulerPage from '@/pages/MetricoolSchedulerPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';

// Video Engine v2 (Phase 2) — dev-only demo page, founder-only.
// NOT linked from any nav. Routed at /v2-demo only.
import VideoEngineV2DemoPage from '@/video_engine_v2/VideoEngineV2DemoPage';
// SEO marketing pages (Round 45)
import WhatIsPayGBotPage from '@/pages/WhatIsPayGBotPage';
import FounderStoryPage from '@/pages/FounderStoryPage';
import BlogIndexPage from '@/pages/BlogIndexPage';
import BlogPostPage from '@/pages/BlogPostPage';

// Bot Pages
import ContentBotPage from '@/pages/bots/ContentBotPage';
import GrowthBotPage from '@/pages/bots/GrowthBotPage';
import MoneyBotPage from '@/pages/bots/MoneyBotPage';
import BusinessBuilderBotPage from '@/pages/bots/BusinessBuilderBotPage';

// Business Brain
import BusinessBrainPage from '@/pages/BusinessBrainPage';

// Brand Brain (Round 113) — persistent visual identity.
import BrandBrainSetupPage from '@/pages/BrandBrainSetupPage';
import BrandReadyPage from '@/pages/BrandReadyPage';

// Sales Page Builder (Round 115) — Universal Builder flagship.
import SalesPageBuilderPage from '@/pages/SalesPageBuilderPage';

// Universal Builders (Round 117 — Phase E3) — every remaining builder.
import UniversalBuildersPage from '@/pages/UniversalBuildersPage';

// Business Operating System (Round 118 — Phase F1) — digital HQ.
import BusinessOSPage from '@/pages/BusinessOSPage';

// Business OS Public Portal (Round 119 — Phase F2) — no auth.
import BusinessOSPortalPage from '@/pages/BusinessOSPortalPage';

// Ask PayGBot page — /ask-paygbot deep-link target for task
// surface helper chips (DailyMissionCard, Weekly Planner,
// Run My Business). Wraps the existing AskPayGBot component;
// no UI in that component is modified.
import AskPayGBotPage from '@/pages/AskPayGBotPage';

// One-time PWA install prompt — gates itself internally on isAuthenticated()
// + mobile viewport + not-already-installed + 7-day dismissal cooldown.
import AddToHomeScreenPrompt from '@/components/AddToHomeScreenPrompt';

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route
            path="/"
            element={
              <PublicRoute>
                <LandingPage />
              </PublicRoute>
            }
          />
          <Route path="/demo" element={<DemoPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          {/* SEO marketing pages (Round 45) — public, indexable */}
          <Route path="/what-is-paygbot" element={<WhatIsPayGBotPage />} />
          <Route path="/founder-story" element={<FounderStoryPage />} />
          <Route path="/blog" element={<BlogIndexPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route
            path="/login"
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            }
          />
          <Route
            path="/signup"
            element={
              <PublicRoute>
                <SignupPage />
              </PublicRoute>
            }
          />
          
          {/* Password Reset - Public route */}
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          {/* Post-checkout landing (both trial signup & trial→paid conversion).
              Refreshes /auth/me so subscription_status flips and downloads
              unlock immediately. */}
          <Route
            path="/subscribe/success"
            element={
              <ProtectedRoute>
                <SubscribeSuccessPage />
              </ProtectedRoute>
            }
          />

          {/* Video Engine v2 dev demo — Phase 2. Founder-only via the
              backend gate. NOT linked from nav. Direct URL only. */}
          <Route
            path="/v2-demo"
            element={
              <ProtectedRoute>
                <VideoEngineV2DemoPage />
              </ProtectedRoute>
            }
          />

          {/* Protected Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          {/* Round 218A P0 (2026-02-07) — the legacy 4-step
              /onboarding wizard has been retired. Business Builder
              Bot IS the onboarding, and it lives inside the
              MonetizationPlanModal on the Dashboard. Any surviving
              link into /onboarding now lands on the Dashboard and
              auto-opens Business Builder Bot via ?openPlan=1. */}
          <Route
            path="/onboarding"
            element={<Navigate to="/dashboard?openPlan=1" replace />}
          />
          <Route
            path="/projects"
            element={<Navigate to="/my-creations" replace />}
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/analytics"
            element={
              <ProtectedRoute>
                <AdminAnalyticsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/operations"
            element={
              <ProtectedRoute>
                <AdminOperationsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/cost-intelligence"
            element={
              <ProtectedRoute>
                <CostIntelligencePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-creations"
            element={
              <ProtectedRoute>
                <MyCreationsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/scheduler"
            element={
              <ProtectedRoute>
                <MetricoolSchedulerPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/weekly-planner"
            element={<Navigate to="/scheduler" replace />}
          />

          {/* Bot Routes */}
          <Route
            path="/bots/content"
            element={
              <ProtectedRoute>
                <ContentBotPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/bots/growth"
            element={
              <ProtectedRoute>
                <GrowthBotPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/bots/money"
            element={
              <ProtectedRoute>
                <MoneyBotPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/bots/business-builder"
            element={
              <ProtectedRoute>
                <BusinessBuilderBotPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/business-brain"
            element={
              <ProtectedRoute>
                <BusinessBrainPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/brand-brain-setup"
            element={
              <ProtectedRoute>
                <BrandBrainSetupPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/brand-ready"
            element={
              <ProtectedRoute>
                <BrandReadyPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sales-page-builder"
            element={
              <ProtectedRoute>
                <SalesPageBuilderPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/universal-builders"
            element={
              <ProtectedRoute>
                <UniversalBuildersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/business-os"
            element={
              <ProtectedRoute>
                <BusinessOSPage />
              </ProtectedRoute>
            }
          />
          {/* Public share portal — no auth wrapper */}
          <Route path="/portal/:token" element={<BusinessOSPortalPage />} />
          <Route
            path="/ask-paygbot"
            element={
              <ProtectedRoute>
                <AskPayGBotPage />
              </ProtectedRoute>
            }
          />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      
      {/* Toast Notifications */}
      <Toaster position="top-right" richColors />

      {/* Platform-wide export upgrade modal — single instance, triggered
          by any download button via `guardExport(...)`. */}

      {/* PWA Add-to-Home-Screen prompt — only renders for authenticated
          mobile users not already running in standalone mode, 30s after
          mount, with a 7-day dismissal cooldown. */}
      <AddToHomeScreenPrompt />
    </div>
  );
}

export default App;
